/**
 * Canonical platform definitions.
 *
 * This is the single source of truth for every platform the composer supports.
 * To add a platform: append an entry here — every consuming component reads
 * from this map, so no other file needs to change for a new platform to appear.
 */

export type PlatformId = 'facebook' | 'instagram';

export interface CharLimits {
  caption: number;
  title?: number;
  description?: number;
  firstComment?: number;
  tags?: number;
}

export interface MediaRule {
  /** How many images can be attached simultaneously. */
  maxImages: number;
  /** How many videos can be attached simultaneously. */
  maxVideos: number;
  /** Max image file size in bytes. */
  maxImageBytes: number;
  /** Max video file size in bytes. */
  maxVideoBytes: number;
  /** MIME types accepted for images. */
  acceptedImageTypes: string[];
  /** MIME types accepted for videos. */
  acceptedVideoTypes: string[];
  /** Minimum aspect ratio (width / height). null = no constraint. */
  minAspectRatio: number | null;
  /** Maximum aspect ratio (width / height). null = no constraint. */
  maxAspectRatio: number | null;
  /** Human-readable notes shown to the user. */
  notes: string[];
}

export interface PlatformFeatures {
  firstComment: boolean;
  location: boolean;
  threadMode: boolean;
  carousel: boolean;
  story: boolean;
  reel: boolean;
  documentUpload: boolean;
  thumbnailUpload: boolean;
  visibility: boolean;
  disableComments: boolean;
  sensitiveContent: boolean;
}

export interface PlatformConfig {
  id: PlatformId;
  name: string;
  /** Tailwind bg class for the platform badge. */
  bgClass: string;
  /** Tailwind text class for the platform badge. */
  textClass: string;
  /** Raw hex for use in inline styles (e.g. SVG). */
  color: string;
  charLimits: CharLimits;
  mediaRule: MediaRule;
  features: PlatformFeatures;
  /** false = "coming soon" — selector shows it disabled. */
  available: boolean;
}

const MB = 1024 * 1024;
const GB = 1024 * MB;

const DEFAULT_FEATURES: PlatformFeatures = {
  firstComment: false,
  location: false,
  threadMode: false,
  carousel: false,
  story: false,
  reel: false,
  documentUpload: false,
  thumbnailUpload: false,
  visibility: false,
  disableComments: false,
  sensitiveContent: false,
};

export const PLATFORM_CONFIGS: Record<PlatformId, PlatformConfig> = {
  facebook: {
    id: 'facebook',
    name: 'Facebook',
    bgClass: 'bg-blue-600',
    textClass: 'text-white',
    color: '#1877F2',
    charLimits: { caption: 63206 },
    mediaRule: {
      maxImages: 10,
      maxVideos: 1,
      maxImageBytes: 30 * MB,
      maxVideoBytes: 4 * GB,
      acceptedImageTypes: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
      acceptedVideoTypes: ['video/mp4', 'video/mov'],
      minAspectRatio: null,
      maxAspectRatio: null,
      notes: ['Up to 10 images', 'Images up to 30 MB', 'Videos up to 4 GB'],
    },
    features: {
      ...DEFAULT_FEATURES,
      carousel: true,
      disableComments: true,
    },
    available: true,
  },

  instagram: {
    id: 'instagram',
    name: 'Instagram',
    bgClass: 'bg-gradient-to-br from-purple-500 via-pink-500 to-orange-400',
    textClass: 'text-white',
    color: '#E1306C',
    charLimits: { caption: 2200, firstComment: 2200 },
    mediaRule: {
      maxImages: 10,
      maxVideos: 1,
      maxImageBytes: 30 * MB,
      maxVideoBytes: 650 * MB,
      acceptedImageTypes: ['image/jpeg', 'image/png'],
      acceptedVideoTypes: ['video/mp4'],
      minAspectRatio: 0.8,
      maxAspectRatio: 1.91,
      notes: ['Aspect ratio 4:5 to 1.91:1', 'Images up to 30 MB', 'Videos up to 650 MB'],
    },
    features: {
      ...DEFAULT_FEATURES,
      firstComment: true,
      location: true,
      carousel: true,
      story: true,
      reel: true,
    },
    available: true,
  },
};

export const ALL_PLATFORMS = Object.values(PLATFORM_CONFIGS);
export const AVAILABLE_PLATFORMS = ALL_PLATFORMS.filter((p) => p.available);
